/* Tuckly card quick add (snippets/tk-deal-card.liquid).
   Progressive enhancement: without JS the card form posts to /cart/add and lands on /cart.
   With JS it adds over AJAX, tells Horizon about it with the same standard event the product form
   uses (so the header count and the cart drawer contents refresh), then opens the cart drawer.
   Loaded as a module from every card; browsers run a module URL only once per page. */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const ADDED_MS = 2200;

/** Section ids of the cart-items components on the page, so the add response carries their fresh HTML. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement ? el.dataset.sectionId : '')
    .filter(Boolean);
}

/* Handle the button click in the capture phase, before the form ever submits: Shopify's analytics script
   intercepts product-form submits and re-posts them with form.submit(), which would bypass a submit listener. */
window.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('form[data-tk-card-add] button[type="submit"]') : null;
  if (!button) return;
  const form = button.form;
  if (!form) return;
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return; // no drawer on this page: keep the normal post
  event.preventDefault();
  event.stopPropagation();

  if (button.getAttribute('aria-busy') === 'true') return;
  const label = button.querySelector('[data-tk-card-add-label]');
  const idle = label ? label.textContent : '';
  button.setAttribute('aria-busy', 'true');

  const body = new FormData(form);
  const sections = cartSectionIds();
  if (sections.length) {
    body.append('sections', sections.join(','));
    body.append('sections_url', window.location.pathname);
  }
  const quantity = Number(body.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(body.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  let addAccepted = false;
  try {
    const response = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    addAccepted = response.ok;
    const added = await response.json();
    const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    const failed = !response.ok || Boolean(added.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'tk-card',
        sourceId: 'tk-card',
        itemCount: quantity,
        sections: failed ? undefined : added.sections,
        didError: failed,
      },
    });
    if (label) label.textContent = failed ? (added.description || 'Could not add') : 'Added';
    const status = form.querySelector('[data-tk-card-add-status]');
    if (status) status.textContent = failed ? (added.description || 'Could not add to cart') : 'Added to cart';
    button.classList.toggle('is-added', !failed);
    setTimeout(() => {
      if (label) label.textContent = idle;
      if (status) status.textContent = '';
      button.classList.remove('is-added');
    }, failed ? 4000 : ADDED_MS);
    if (!failed && !drawer.isOpen) drawer.open();
  } catch (error) {
    deferred.reject(error);
    // Network trouble. If the add itself went through (only the follow-up cart read failed), go to the cart
    // instead of posting the form again, which would add the item a second time.
    if (addAccepted) window.location.assign(root + 'cart');
    else form.submit(); // fall back to the plain cart post
  } finally {
    button.removeAttribute('aria-busy');
  }
}, true);
